import { Stack, Text } from '@fluentui/react';
import { Label, TagItemSuggestion, TagPicker } from 'office-ui-fabric-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NotificationsTarget, useNotificationsManager } from '../../contexts/notificationContext';
import { DataContact } from '../../data/DataContact';
import { MaxLengths } from '../../data/MaxLenghts';
import { Immocom } from '../../specifique/immocom';

const PickerContact = (props) => {

    // CONTEXTS
    const { extract } = useNotificationsManager();

    // PROPS
    const {
        disabled = false,
        idContact = undefined,
        label = "Nom",
        maxLength = MaxLengths.contact.nom,
        nomContact = undefined,
        required = false,
        /* --- */
        onBlur = (newValue) => null, // Saisie manuelle
        onChange = (contact) => null, // Sélection d'un contact parmi ceux retournés par la recherche
        onInputChange = (newValue) => null, // Sélection d'un contact parmi ceux retournés par la recherche
    } = props;

    // CONSTANTES

    // STATES
    const [_selectedItems, _setSelectedItems] = useState();

    // REFS
    const _isMounted = useRef(false); // Utilisé pour bloquer certains useEffect au premier render

    // MEMOS

    // METHODS
    const _chargementDonnees = useCallback(() => {
        if (!!idContact)
            DataContact._chargementPickerContact(idContact).then(response => {
                const onSuccess = () => {
                    const contact = response.reponseAPIobjet;
                    const { idContact, nom } = contact;
                    const items = [{
                        key: idContact,
                        name: nom
                    }];
                    _setSelectedItems(items);
                };
                const onError = (result) => extract(result, NotificationsTarget.panels);
                Immocom.checkFetchResponse(response, onSuccess, onError);
            });
    }, [idContact]);

    const _onBlur = useCallback((event) => {
        if (typeof onBlur === "function")
            onBlur(event.target.value);
    }, []);

    const _onChange = useCallback((items) => {
        _setSelectedItems(items);
        if (typeof onChange === "function") {
            if (Array.isArray(items) && items.length === 1) {
                const { contact } = items[0];
                onChange(contact);
            }
            else onChange(null);
        }
    }, []);

    const _onInputChange = useCallback((input) => {
        input = input
            .replace(/[^a-zA-Z0-9'\-àâäéèêëïîôöùûüÿçÀÂÄÉÈÊËÏÎÔÖÙÛÜŸÇ ]+/g, "")
            .replace(/\s\s+/g, " "); // Remove multiple whitespaces
        if (typeof onInputChange === "function")
            onInputChange(input);
        return input;
    }, []);

    const _onResolveSuggestions = useCallback((filterText, tagList) => {
        if (!!filterText && filterText.length >= 3)
            return DataContact._getContactRecherche(filterText).then(response => {
                const onSuccess = () => {
                    const contacts = response.reponseAPIobjet;
                    if (!Array.isArray(contacts)) return [];
                    else return contacts.map(contact => {
                        const { idContact, nom } = contact;
                        return {
                            key: idContact,
                            name: nom,
                            contact
                        };
                    });
                };
                const onError = (result) => extract(result, NotificationsTarget.panels);
                return Immocom.checkFetchResponse(response, onSuccess, onError);
            });
        else return [];
    }, []);

    // EFFECTS (ComponentDidUpdate)
    useEffect(() => {
        if (_isMounted.current)
            if (!!idContact) _chargementDonnees();
    }, [_isMounted, idContact]);

    // EFFECTS (ComponentDidMount)
    useEffect(() => {
        _isMounted.current = true;
    }, []);

    // UI
    const _renderedLabel = useMemo(() => {
        if (!label) return null;
        else {
            const renderedLabel = <Label disabled={disabled} required={required}>{label}</Label>;
            if (!disabled && !!maxLength && Number.isInteger(maxLength)) {
                const nbCarRestants = Immocom.fiche().getNbCarRestants(nomContact, maxLength);
                return <Stack horizontal horizontalAlign="space-between" verticalAlign="end">
                    {renderedLabel}
                    <Text variant="small">{`${nbCarRestants}/${maxLength}`}</Text>
                </Stack>
            }
            else return renderedLabel;
        }
    }, [disabled, label, maxLength, nomContact, required]);

    // RENDER
    return <Stack>
        {_renderedLabel}
        <TagPicker
            disabled={disabled}
            inputProps={{ value: nomContact || " " }} // Espace ; bypass un bug de FluentUI qui ne rerender pas correctement l'input s'il est vide
            itemLimit={1}
            onBlur={_onBlur}
            onChange={_onChange}
            onInputChange={_onInputChange}
            onRenderSuggestionsItem={(props) => {
                const { civilite, nom, prenom, cp } = props.contact;
                return <TagItemSuggestion>{[civilite, nom, prenom, cp].filter(data => !!data).join(" ")}</TagItemSuggestion>;
            }}
            onResolveSuggestions={_onResolveSuggestions}
            pickerSuggestionsProps={{ noResultsFoundText: "", suggestionsHeaderText: "" }}
            resolveDelay={500}
            selectedItems={_selectedItems}
            styles={{ input: { "text-transform": "uppercase" } }}
        />
    </Stack>;

};

export default PickerContact;